import { useEffect, useState, useMemo } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { api, formatError, BACKEND_URL } from "@/lib/api";
import { LEAD_STATUSES, statusPillClass, STATES, JOB_TYPES } from "@/lib/constants";
import { Loader2, LogOut, Download, X, RefreshCcw, Search, Home as HomeIcon, Users, LayoutDashboard, DollarSign, Save } from "lucide-react";

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white border border-border p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-4xl text-asphalt">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  return <span className={`inline-block px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${statusPillClass(status)}`}>{status}</span>;
}

export default function AdminDashboard() {
  const { user, checking, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ status: "", suburb: "", state: "", job_type: "", q: "" });
  const [tab, setTab] = useState("leads"); // leads | contractors
  const [contractors, setContractors] = useState([]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/admin/leads", { params });
      setLeads(data);
    } catch (err) { toast.error(formatError(err)); } finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (err) { /* ignore */ }
  };

  const fetchContractors = async () => {
    try {
      const { data } = await api.get("/admin/contractors");
      setContractors(data);
    } catch (err) { toast.error(formatError(err)); }
  };

  // Only refetch when the authenticated user changes, not on every re-render.
  // fetchLeads/fetchStats/fetchContractors are stable closures over state setters and
  // adding them to the deps would cause an infinite refetch loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (user) { fetchLeads(); fetchStats(); fetchContractors(); } }, [user]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/leads/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "readysite-leads.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatError(err)); }
  };

  const updateLead = async (id, patch) => {
    try {
      const { data } = await api.patch(`/admin/leads/${id}`, patch);
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
      if (selected?.id === id) setSelected(data);
      fetchStats();
      toast.success("Lead updated");
    } catch (err) { toast.error(formatError(err)); }
  };

  const filtered = useMemo(() => leads, [leads]);

  if (checking) return <div className="min-h-screen grid place-items-center">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-concrete">
      {/* Top bar */}
      <div className="bg-asphalt text-white">
        <div className="rs-container flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <span className="inline-block bg-white p-1">
              <img src="/logo.png" alt="Site Cleanup" className="h-7 w-auto block" />
            </span>
            <span className="font-display uppercase text-sm md:text-base text-white/60">Admin</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/" className="hidden md:flex items-center gap-2 text-white/70 hover:text-white" data-testid="admin-view-site"><HomeIcon size={14}/> View site</Link>
            <span className="text-white/50 hidden md:inline">·</span>
            <span className="text-white/80">{user.email}</span>
            <button data-testid="admin-logout" onClick={logout} className="ml-2 inline-flex items-center gap-1.5 border border-white/30 px-3 py-1.5 text-xs hover:border-safety hover:text-safety"><LogOut size={13}/> Sign out</button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border bg-white">
        <div className="rs-container flex items-center gap-6">
          {[
            { id: "leads", label: "Leads", icon: LayoutDashboard },
            { id: "contractors", label: "Contractors", icon: Users },
            { id: "rates", label: "Pricing rates", icon: DollarSign },
            { id: "skipbins", label: "Skip bins", icon: DollarSign },
          ].map(({ id, label, icon: Ic }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`py-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider border-b-2 ${tab === id ? "border-safety text-safety" : "border-transparent text-muted-foreground hover:text-asphalt"}`}>
              <Ic size={16}/> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="rs-container py-8">
        {tab === "leads" && (
          <>
            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <Stat label="Total leads" value={stats.total} testid="stat-total" />
                <Stat label="New" value={stats.new} testid="stat-new" />
                <Stat label="Won" value={stats.won} testid="stat-won" />
                <Stat label="Lost" value={stats.lost} testid="stat-lost" />
              </div>
            )}

            {/* Filters */}
            <div className="bg-white border border-border p-4 mb-4 grid grid-cols-1 md:grid-cols-6 gap-3">
              <div className="relative md:col-span-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
                <input data-testid="filter-q" placeholder="Search name / email / phone" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className="rs-input pl-9" />
              </div>
              <select data-testid="filter-status" className="rs-input" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                <option value="">All statuses</option>
                {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input data-testid="filter-suburb" placeholder="Suburb" className="rs-input" value={filters.suburb} onChange={(e) => setFilters({ ...filters, suburb: e.target.value })} />
              <select data-testid="filter-state" className="rs-input" value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })}>
                <option value="">All states</option>
                {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select data-testid="filter-job_type" className="rs-input" value={filters.job_type} onChange={(e) => setFilters({ ...filters, job_type: e.target.value })}>
                <option value="">All job types</option>
                {JOB_TYPES.map((j) => <option key={j} value={j}>{j}</option>)}
              </select>
            </div>

            <div className="flex items-center gap-2 mb-4">
              <button onClick={fetchLeads} className="rs-btn-outline text-sm py-2 px-4" data-testid="apply-filters"><RefreshCcw size={14} className="mr-2 inline"/> Apply</button>
              <button onClick={exportCsv} className="rs-btn-primary text-sm py-2 px-4" data-testid="export-csv"><Download size={14} className="mr-2 inline"/> Export CSV</button>
            </div>

            {/* Table */}
            <div className="bg-white border border-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left uppercase text-xs tracking-widest text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Ref</th>
                    <th className="px-4 py-3">Received</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Contact</th>
                    <th className="px-4 py-3">Suburb</th>
                    <th className="px-4 py-3">Job</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={8} className="p-8 text-center text-muted-foreground"><Loader2 className="animate-spin inline mr-2" size={16}/> Loading…</td></tr>}
                  {!loading && filtered.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No leads yet.</td></tr>}
                  {!loading && filtered.map((l) => (
                    <tr key={l.id} className="border-t border-border hover:bg-concrete cursor-pointer" onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`}>
                      <td className="px-4 py-3 font-mono text-safety text-[13px] tracking-widest">{l.reference || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{new Date(l.created_at).toLocaleDateString('en-AU')}<br/><span className="text-[11px]">{new Date(l.created_at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</span></td>
                      <td className="px-4 py-3 font-semibold text-asphalt">{l.full_name}</td>
                      <td className="px-4 py-3 text-xs">{l.email}<br/>{l.phone}</td>
                      <td className="px-4 py-3">{l.suburb} <span className="text-muted-foreground">{l.state}</span></td>
                      <td className="px-4 py-3 text-xs">{l.job_type}</td>
                      <td className="px-4 py-3"><StatusPill status={l.status}/></td>
                      <td className="px-4 py-3 text-safety text-xs font-semibold">Open →</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Detail drawer */}
            {selected && (
              <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={() => setSelected(null)}>
                <div className="w-full max-w-2xl bg-white h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="lead-drawer">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <span className="rs-eyebrow">Lead detail</span>
                      <h2 className="font-display text-3xl uppercase mt-1">{selected.full_name}</h2>
                      <p className="text-xs text-muted-foreground mt-1">
                        Ref <strong className="font-mono text-safety">{selected.reference || "—"}</strong>
                        <span className="mx-2">·</span>
                        ID: <span className="opacity-70">{selected.id}</span>
                      </p>
                    </div>
                    <button onClick={() => setSelected(null)} className="p-2 border border-border hover:border-safety" data-testid="drawer-close"><X size={16}/></button>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm mb-6">
                    <div><span className="rs-label">Status</span>
                      <select data-testid="drawer-status" className="rs-input" value={selected.status} onChange={(e) => updateLead(selected.id, { status: e.target.value })}>
                        {LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div><span className="rs-label">Assigned contractor</span>
                      <input data-testid="drawer-contractor" className="rs-input" defaultValue={selected.assigned_contractor} onBlur={(e) => e.target.value !== selected.assigned_contractor && updateLead(selected.id, { assigned_contractor: e.target.value })} />
                    </div>
                  </div>

                  {/* Payment status pills */}
                  <div className="flex flex-wrap gap-2 mb-4 text-[11px] uppercase tracking-widest">
                    {selected.unlocked_by ? (
                      <span className="px-2 py-1 bg-green-100 text-green-800" data-testid="pill-unlocked">Unlocked by {selected.unlocked_by}</span>
                    ) : (
                      <span className="px-2 py-1 bg-muted text-muted-foreground">Not yet unlocked</span>
                    )}
                    {selected.deposit_paid && <span className="px-2 py-1 bg-green-100 text-green-800" data-testid="pill-deposit">Deposit paid</span>}
                  </div>

                  {/* Payment action buttons */}
                  <div className="grid grid-cols-2 gap-2 mb-6">
                    <button
                      data-testid="btn-copy-unlock"
                      onClick={() => {
                        const email = selected.assigned_contractor || "";
                        const url = `${window.location.origin}/pay/unlock?lead=${selected.id}${email ? `&contractor=${encodeURIComponent(email)}` : ""}`;
                        navigator.clipboard.writeText(url);
                        toast.success("Contractor unlock link copied");
                      }}
                      className="rs-btn-outline text-xs py-2"
                    >Copy contractor unlock link (A$30)</button>
                    <button
                      data-testid="btn-copy-deposit"
                      onClick={() => {
                        const url = `${window.location.origin}/pay/deposit?lead=${selected.id}`;
                        navigator.clipboard.writeText(url);
                        toast.success("Customer deposit link copied");
                      }}
                      className="rs-btn-outline text-xs py-2"
                    >Copy customer deposit link (A$49)</button>
                  </div>

                  <div className="space-y-2 text-sm mb-6">
                    <div><span className="text-muted-foreground">Email:</span> {selected.email}</div>
                    <div><span className="text-muted-foreground">Phone:</span> {selected.phone}</div>
                    <div><span className="text-muted-foreground">Customer type:</span> {selected.customer_type}</div>
                    <div><span className="text-muted-foreground">Site:</span> {selected.site_address}, {selected.suburb} {selected.state} {selected.postcode}</div>
                    <div><span className="text-muted-foreground">Job type:</span> {selected.job_type}</div>
                    <div><span className="text-muted-foreground">Site size:</span> {selected.site_size || '-'}</div>
                    <div><span className="text-muted-foreground">Timeframe:</span> {selected.timeframe}</div>
                    {selected.service_kind === "skip_bin" && selected.skip_bin_size && (
                      <div className="pt-2 border-t border-border">
                        <span className="text-muted-foreground">Skip bin:</span>{" "}
                        <strong>{selected.skip_bin_size}</strong> · {selected.skip_bin_waste || "general"} — indicative price{" "}
                        <strong className="text-safety">
                          {selected.estimate_min != null ? `A$${Math.round(selected.estimate_min).toLocaleString()}` : "-"}
                        </strong>
                      </div>
                    )}
                    {selected.service_kind !== "skip_bin" && selected.service_category && (
                      <div className="pt-2 border-t border-border">
                        <span className="text-muted-foreground">Customer estimate:</span>{" "}
                        <strong>{selected.service_category}</strong> @ {selected.size || "-"} — indicative range{" "}
                        <strong className="text-safety">
                          {selected.estimate_min != null ? `A$${Math.round(selected.estimate_min).toLocaleString()}` : "-"}
                          {" – "}
                          {selected.estimate_max != null ? `A$${Math.round(selected.estimate_max).toLocaleString()}` : "-"}
                        </strong>
                      </div>
                    )}
                    <div><span className="text-muted-foreground">Construction waste:</span> {selected.has_construction_waste} · <span className="text-muted-foreground">Green waste:</span> {selected.has_green_waste} · <span className="text-muted-foreground">Soil/rubble:</span> {selected.has_soil_rubble} · <span className="text-muted-foreground">Hazardous:</span> {selected.has_hazardous}</div>
                    {selected.message && <div className="pt-2 border-t border-border"><span className="text-muted-foreground">Message:</span> <p className="mt-1">{selected.message}</p></div>}
                  </div>

                  {selected.photo_urls?.length > 0 && (
                    <div className="mb-6">
                      <span className="rs-label">Photos</span>
                      <div className="grid grid-cols-3 gap-2 mt-1">
                        {selected.photo_urls.map((u) => (
                          <a key={u} href={u.startsWith('/api') ? `${BACKEND_URL}${u}` : u} target="_blank" rel="noreferrer" className="block border border-border">
                            <img src={u.startsWith('/api') ? `${BACKEND_URL}${u}` : u} className="w-full h-24 object-cover" alt="site" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <span className="rs-label">Internal notes</span>
                    <textarea data-testid="drawer-notes" rows={4} defaultValue={selected.internal_notes} onBlur={(e) => e.target.value !== selected.internal_notes && updateLead(selected.id, { internal_notes: e.target.value })} className="rs-input" placeholder="Add notes about this lead…"/>
                    <p className="text-xs text-muted-foreground mt-1">Auto-saves on blur.</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {tab === "contractors" && (
          <div className="bg-white border border-border overflow-x-auto" data-testid="contractors-table">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left uppercase text-xs tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Received</th>
                  <th className="px-4 py-3">Business</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Service areas</th>
                  <th className="px-4 py-3">Services offered</th>
                </tr>
              </thead>
              <tbody>
                {contractors.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No contractor applications yet.</td></tr>}
                {contractors.map((c) => (
                  <tr key={c.id} className="border-t border-border align-top">
                    <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString('en-AU')}</td>
                    <td className="px-4 py-3 font-semibold">{c.business_name}<br/><span className="text-xs text-muted-foreground">{c.abn}</span></td>
                    <td className="px-4 py-3 text-xs">{c.contact_person}<br/>{c.email}<br/>{c.phone}</td>
                    <td className="px-4 py-3 text-xs max-w-xs">{c.service_areas}</td>
                    <td className="px-4 py-3 text-xs max-w-md">{c.services_offered}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "rates" && <PricingRatesPanel />}
        {tab === "skipbins" && <SkipBinsPanel />}
      </div>
    </div>
  );
}

function PricingRatesPanel() {
  const [rates, setRates] = useState(null);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    api.get("/admin/pricing-rates").then((r) => { setRates(r.data.rates); setMeta(r.data.updated_at); }).catch((e) => toast.error(formatError(e)));
  }, []);

  const update = (cat, field, value) => setRates((prev) => ({ ...prev, [cat]: { ...prev[cat], [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      // Send min/max as numbers; label/note/unit as strings
      const payload = {};
      Object.entries(rates).forEach(([cat, r]) => {
        payload[cat] = { min: Number(r.min), max: Number(r.max), label: r.label, note: r.note, unit: r.unit };
      });
      const { data } = await api.patch("/admin/pricing-rates", { rates: payload });
      setRates(data.rates);
      toast.success("Pricing rates saved");
    } catch (e) { toast.error(formatError(e)); } finally { setSaving(false); }
  };

  if (!rates) return <div className="p-8 text-center text-muted-foreground"><Loader2 className="animate-spin inline mr-2" size={16}/> Loading rates…</div>;

  return (
    <div className="space-y-6" data-testid="admin-rates-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl uppercase">Indicative pricing rates</h2>
          <p className="text-sm text-muted-foreground mt-1">These values drive the live estimate shown on the public quote form. Update whenever Melbourne market rates change.</p>
          {meta && <p className="text-[11px] text-muted-foreground mt-1">Last updated: {new Date(meta).toLocaleString('en-AU')}</p>}
        </div>
        <button onClick={save} disabled={saving} className="rs-btn-primary text-sm py-2 px-5 disabled:opacity-70" data-testid="admin-rates-save">
          {saving ? <><Loader2 className="animate-spin mr-2" size={14}/> Saving…</> : <><Save size={14} className="mr-2"/> Save rates</>}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(rates).map(([cat, r]) => (
          <div key={cat} className="bg-white border border-border p-5" data-testid={`admin-rate-${cat}`}>
            <label className="block">
              <span className="rs-label">Category label</span>
              <input
                data-testid={`admin-rate-${cat}-label`}
                className="rs-input font-semibold"
                value={r.label || ""}
                onChange={(e) => update(cat, "label", e.target.value)}
              />
            </label>
            <div className="grid grid-cols-3 gap-3 mt-3">
              <label className="block">
                <span className="rs-label">Unit</span>
                <input data-testid={`admin-rate-${cat}-unit`} className="rs-input text-center" value={r.unit || ""} onChange={(e) => update(cat, "unit", e.target.value)} placeholder="m²" />
              </label>
              <label className="block">
                <span className="rs-label">Min $/{r.unit || "unit"}</span>
                <input data-testid={`admin-rate-${cat}-min`} type="number" step="0.01" min="0" className="rs-input" value={r.min ?? 0} onChange={(e) => update(cat, "min", e.target.value)} />
              </label>
              <label className="block">
                <span className="rs-label">Max $/{r.unit || "unit"}</span>
                <input data-testid={`admin-rate-${cat}-max`} type="number" step="0.01" min="0" className="rs-input" value={r.max ?? 0} onChange={(e) => update(cat, "max", e.target.value)} />
              </label>
            </div>
            <label className="block mt-3">
              <span className="rs-label">Note shown to customer</span>
              <textarea data-testid={`admin-rate-${cat}-note`} rows={2} className="rs-input" value={r.note || ""} onChange={(e) => update(cat, "note", e.target.value)} />
            </label>
          </div>
        ))}
      </div>

      <p className="text-xs text-muted-foreground">
        Changes take effect immediately on the public quote form. If min &gt; max, saving is rejected server-side.
      </p>
    </div>
  );
}

function SkipBinsPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    api.get("/admin/skip-bin-config")
      .then((r) => { setCfg(r.data); setMeta(r.data.updated_at); })
      .catch((e) => toast.error(formatError(e)));
  }, []);

  const updSize = (code, field, value) =>
    setCfg((prev) => ({
      ...prev,
      sizes: prev.sizes.map((s) => (s.code === code ? { ...s, [field]: value } : s)),
    }));

  const updField = (field, value) => setCfg((prev) => ({ ...prev, [field]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        hire_days: Number(cfg.hire_days),
        extra_day_fee_min: Number(cfg.extra_day_fee_min),
        extra_day_fee_max: Number(cfg.extra_day_fee_max),
        sizes: cfg.sizes.map((s) => ({
          code: s.code,
          label: s.label,
          capacity: s.capacity,
          best_for: s.best_for,
          min: Number(s.min),
          max: Number(s.max),
        })),
      };
      const { data } = await api.patch("/admin/skip-bin-config", payload);
      setCfg(data);
      setMeta(data.updated_at);
      toast.success("Skip bin config saved");
    } catch (e) { toast.error(formatError(e)); } finally { setSaving(false); }
  };

  if (!cfg) return <div className="p-8 text-center text-muted-foreground"><Loader2 className="animate-spin inline mr-2" size={16}/> Loading skip bin config…</div>;

  return (
    <div className="space-y-6" data-testid="admin-skipbins-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl uppercase">Skip bin rates</h2>
          <p className="text-sm text-muted-foreground mt-1">Edit the min–max range for each skip size and the extra-day fee. Ranges show on the public estimator immediately.</p>
          {meta && <p className="text-[11px] text-muted-foreground mt-1">Last updated: {new Date(meta).toLocaleString('en-AU')}</p>}
        </div>
        <button onClick={save} disabled={saving} className="rs-btn-primary text-sm py-2 px-5 disabled:opacity-70" data-testid="admin-skipbins-save">
          {saving ? <><Loader2 className="animate-spin mr-2" size={14}/> Saving…</> : <><Save size={14} className="mr-2"/> Save skip bin rates</>}
        </button>
      </div>

      <div className="bg-white border border-border p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <label className="block">
          <span className="rs-label">Standard hire (days)</span>
          <input data-testid="admin-skipbins-hire-days" type="number" min="1" max="60" className="rs-input" value={cfg.hire_days} onChange={(e) => updField("hire_days", e.target.value)} />
        </label>
        <label className="block">
          <span className="rs-label">Extra day fee — min ($)</span>
          <input data-testid="admin-skipbins-extra-min" type="number" min="0" step="1" className="rs-input" value={cfg.extra_day_fee_min} onChange={(e) => updField("extra_day_fee_min", e.target.value)} />
        </label>
        <label className="block">
          <span className="rs-label">Extra day fee — max ($)</span>
          <input data-testid="admin-skipbins-extra-max" type="number" min="0" step="1" className="rs-input" value={cfg.extra_day_fee_max} onChange={(e) => updField("extra_day_fee_max", e.target.value)} />
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cfg.sizes.map((s) => (
          <div key={s.code} className="bg-white border border-border p-5" data-testid={`admin-skipbin-${s.code}`}>
            <label className="block">
              <span className="rs-label">Size label ({s.code})</span>
              <input data-testid={`admin-skipbin-${s.code}-label`} className="rs-input font-semibold" value={s.label || ""} onChange={(e) => updSize(s.code, "label", e.target.value)} />
            </label>
            <label className="block mt-3">
              <span className="rs-label">Capacity description</span>
              <input data-testid={`admin-skipbin-${s.code}-capacity`} className="rs-input" value={s.capacity || ""} onChange={(e) => updSize(s.code, "capacity", e.target.value)} />
            </label>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <label className="block">
                <span className="rs-label">Min $ (standard)</span>
                <input data-testid={`admin-skipbin-${s.code}-min`} type="number" min="0" step="1" className="rs-input" value={s.min ?? 0} onChange={(e) => updSize(s.code, "min", e.target.value)} />
              </label>
              <label className="block">
                <span className="rs-label">Max $ (premium)</span>
                <input data-testid={`admin-skipbin-${s.code}-max`} type="number" min="0" step="1" className="rs-input" value={s.max ?? 0} onChange={(e) => updSize(s.code, "max", e.target.value)} />
              </label>
            </div>
            <label className="block mt-3">
              <span className="rs-label">"Best for" description</span>
              <textarea data-testid={`admin-skipbin-${s.code}-best`} rows={2} className="rs-input" value={s.best_for || ""} onChange={(e) => updSize(s.code, "best_for", e.target.value)} />
            </label>
          </div>
        ))}
      </div>

      <p className="text-xs text-muted-foreground">
        Changes take effect immediately on the public quote form. If min &gt; max, saving is rejected server-side.
      </p>
    </div>
  );
}

